import React from 'react';
import styled from 'styled-components';

// Page Container
export const PageContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
  color: ${props => props.theme.foreground};
`;

// Content Container
export const ContentContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 24px;
  
  @media (min-width: 640px) {
    padding: 32px;
  }
`;

// Page Header
export const PageHeader = styled.div`
  margin-bottom: 24px;
`;

export const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.foreground};
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

export const PageDescription = styled.p`
  color: ${props => props.theme.mutedForeground};
  font-size: 1rem;
  line-height: 1.5;
`;

// Grid Layouts
export const Grid = styled.div<{ $cols?: number; $gap?: string }>`
  display: grid;
  grid-template-columns: repeat(${props => props.$cols || 1}, 1fr);
  gap: ${props => props.$gap || '16px'};
`;

export const Grid2 = styled(Grid).attrs({ $cols: 2 })``;
export const Grid3 = styled(Grid).attrs({ $cols: 3 })``;
export const Grid4 = styled(Grid).attrs({ $cols: 4 })``;

export const ResponsiveGrid = styled.div<{ $cols: number[] }>`
  display: grid;
  gap: 16px;
  
  ${props => {
    const [sm, md, lg, xl] = props.$cols;
    return `
      grid-template-columns: repeat(${sm}, 1fr);
      
      @media (min-width: 640px) {
        grid-template-columns: repeat(${md || sm}, 1fr);
      }
      
      @media (min-width: 1024px) {
        grid-template-columns: repeat(${lg || md || sm}, 1fr);
      }
      
      @media (min-width: 1280px) {
        grid-template-columns: repeat(${xl || lg || md || sm}, 1fr);
      }
    `;
  }}
`;

// Flex Layouts
export const Flex = styled.div<{ $direction?: string; $align?: string; $justify?: string; $gap?: string }>`
  display: flex;
  flex-direction: ${props => props.$direction || 'row'};
  align-items: ${props => props.$align || 'stretch'};
  justify-content: ${props => props.$justify || 'flex-start'};
  gap: ${props => props.$gap || '0'};
`;

export const FlexCenter = styled(Flex).attrs({ $align: 'center', $justify: 'center' })``;
export const FlexBetween = styled(Flex).attrs({ $justify: 'space-between' })``;
export const FlexColumn = styled(Flex).attrs({ $direction: 'column' })``;
export const FlexColumnCenter = styled(Flex).attrs({ $direction: 'column', $align: 'center' })``;

// Section Components
export const Section = styled.section`
  margin-bottom: 32px;
`;

export const SectionHeader = styled.div`
  margin-bottom: 16px;
`;

export const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.foreground};
  margin-bottom: 8px;
`;

export const SectionDescription = styled.p`
  color: ${props => props.theme.mutedForeground};
  font-size: 0.875rem;
  line-height: 1.5;
`;

// Card Layout
export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

// Responsive Container
export const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0 1rem;
  
  @media (min-width: 640px) {
    max-width: 640px;
    padding: 0 1.5rem;
  }
  
  @media (min-width: 768px) {
    max-width: 768px;
  }
  
  @media (min-width: 1024px) {
    max-width: 1024px;
  }
  
  @media (min-width: 1280px) {
    max-width: 1280px;
  }
`;

// Spacer Components
export const Spacer = styled.div<{ $size?: string }>`
  height: ${props => props.$size || '1rem'};
`;

export const SpacerSm = styled(Spacer).attrs({ $size: '0.5rem' })``;
export const SpacerMd = styled(Spacer).attrs({ $size: '1rem' })``;
export const SpacerLg = styled(Spacer).attrs({ $size: '1.5rem' })``;
export const SpacerXl = styled(Spacer).attrs({ $size: '2rem' })``;

// Divider
export const Divider = styled.hr`
  border: none;
  height: 1px;
  background: ${props => props.theme.border};
  margin: 1rem 0;
`;

// Responsive Hide/Show
export const HideOnMobile = styled.div`
  @media (max-width: 640px) {
    display: none;
  }
`;

export const ShowOnMobile = styled.div`
  display: none;
  
  @media (max-width: 640px) {
    display: block;
  }
`;

export const HideOnTablet = styled.div`
  @media (max-width: 1024px) {
    display: none;
  }
`;

export const ShowOnTablet = styled.div`
  display: none;
  
  @media (max-width: 1024px) {
    display: block;
  }
`; 